import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@components/layout/page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useGetAdminExceedances } from '@utils/api/back-office/exceedances/use-get-admin-exceedances';
import { AdminExceedanceSummary } from '@utils/types/back-office/exceedances';
import { PATHS } from '../../router/paths';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

type ExceedanceRow = AdminExceedanceSummary & { id: number };

export default function ExceedancesBo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour } = useThemeContext();
  const { data, isLoading } = useGetAdminExceedances();

  const columns = useMemo<DataGridColumn<ExceedanceRow>[]>(
    () => [
      {
        id: 'organization_name',
        accessorKey: 'organization_name',
        header: t('organization'),
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'organization_name',
        serverOperator: 'ilike',
        cell: info => {
          const row = info.row.original;
          return (
            <button
              className="text-left underline hover:opacity-80"
              style={{ color: getColour('activeText') }}
              onClick={() =>
                navigate(
                  PATHS(String(row.organization_id)).CUSTOMER_EXCEEDANCES
                )
              }
            >
              {row.organization_name}
            </button>
          );
        }
      },
      {
        id: 'exceedance_count',
        accessorKey: 'exceedance_count',
        header: t('exceedance-count'),
        maxWidth: 160,
        enableSorting: true,
        enableFiltering: false
      },
      {
        id: 'types',
        accessorKey: 'types',
        header: t('types'),
        maxWidth: 220,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'type',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: 'STT', value: 'stt' },
          { label: 'Translation', value: 'translation' }
        ],
        cell: info => {
          const types = info.getValue() as string[];
          return (
            <div className="flex flex-wrap gap-1.5">
              {types.map(type => (
                <StatusBadge key={type} status={type} label={t(type)} />
              ))}
            </div>
          );
        }
      }
    ],
    [t, navigate, getColour]
  );

  const rows: ExceedanceRow[] = useMemo(
    () =>
      (data?.result || []).map(item => ({
        ...item,
        id: item.organization_id
      })),
    [data]
  );

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      <PageHeader
        title={t('plan-exceedances')}
        caption={t('organizations-exceeding-plan-limits')}
      />
      <div className="mt-4 flex-1 min-h-0 flex flex-col">
        <DataGridV2<ExceedanceRow>
          fill
          columns={columns}
          data={rows}
          enablePagination
          manualPagination
          totalItems={data?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          enableRowSelection={false}
          dense
          striped
          grid={false}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
